import { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { navBg } from './constants';
import { visuallyHiddenSx } from '../layout/visually-hidden';
import { broadcastLogout } from './use-session-broadcast';
import type { ReactNode } from 'react';
import type { AppShellAccount, UserMenuItem } from './types';

const accountPath = '/profile';
const headerSx = { borderBottom: 1, borderColor: 'divider', px: '14px', py: '12px' };
const itemSx = { px: '14px', py: '8px', fontSize: '14px', color: 'text.secondary' };

function getInitials(account: AppShellAccount | null): string {
  if (!account) {
    return '';
  }
  const parts: string[] = [];
  if (account.firstName) {
    parts.push(account.firstName.charAt(0));
  }
  if (account.lastName) {
    parts.push(account.lastName.charAt(0));
  }
  return parts.join('').toUpperCase();
}

type UserMenuProps = {
  account: AppShellAccount | null;
  /**
   * The only part of the menu an app owns: its own pages, rendered top-to-bottom with a divider
   * between each group. The identity header above and the log out below belong to every app, so
   * no app supplies either. Empty groups are skipped.
   */
  itemGroups: UserMenuItem[][];
  /** Run-as has no session of its own to end, so the item stands down rather than disappearing. */
  logoutDisabled?: boolean;
  emitDocAttributes: boolean;
};

export function UserMenu({ account, itemGroups, logoutDisabled = false, emitDocAttributes }: UserMenuProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const initials = getInitials(account);
  const fullName = [account?.firstName, account?.lastName].filter(Boolean).join(' ') || 'User profile';

  const triggerDocProps = emitDocAttributes ? { 'data-doc-action': 'open-user-menu' } : {};

  const populatedGroups = itemGroups.filter((group) => group.length > 0);

  // Who you are is also the way to your own account, so the header is the link rather than a
  // label above one. An app that does not know where the account hub lives keeps the label.
  let header: ReactNode = null;
  if (account && account.email) {
    const lines = (
      <>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'text.primary' }}
        >
          {account.firstName} {account.lastName}
        </Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
          {account.email}
        </Typography>
      </>
    );
    header = <Box sx={headerSx}>{lines}</Box>;
    if (typeof account.accountHubBaseUrl === 'string') {
      const accountHref = `${account.accountHubBaseUrl}${accountPath}`;
      const headerDocProps = emitDocAttributes
        ? { 'data-doc-action': 'navigate', 'data-doc-context': accountHref }
        : {};
      header = (
        <MenuItem component="a" href={accountHref} {...headerDocProps} sx={{ ...headerSx, display: 'block' }}>
          {/* The name and email are who you are, not where the link goes, so the destination is
              named for anyone who hears the link rather than seeing the menu it sits in. */}
          <Box component="span" sx={visuallyHiddenSx}>
            Your account:
          </Box>
          {lines}
        </MenuItem>
      );
    }
  }

  // The one item every app's menu ends with, built here rather than passed in, so no app can
  // ship a menu without a way out and no two apps can call it something different.
  let logout: ReactNode = null;
  if (account && account.accountHubLogoutUrl) {
    const logoutDocProps = emitDocAttributes
      ? { 'data-doc-action': 'navigate', 'data-doc-context': account.accountHubLogoutUrl }
      : {};
    logout = (
      <MenuItem
        component="a"
        href={account.accountHubLogoutUrl}
        {...logoutDocProps}
        onClick={() => broadcastLogout()}
        sx={itemSx}
      >
        Log out
      </MenuItem>
    );
    if (logoutDisabled) {
      logout = (
        <MenuItem disabled sx={itemSx}>
          Log out
        </MenuItem>
      );
    }
  }

  return (
    <>
      <IconButton
        aria-label="User menu"
        {...triggerDocProps}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        sx={{
          p: 0,
          border: 2,
          borderColor: 'divider',
          transition: 'border-color 0.2s',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'transparent' },
        }}
      >
        <Avatar
          src={account?.profilePhotoUrl || undefined}
          alt={fullName}
          sx={{ width: 32, height: 32, bgcolor: '#fff', color: navBg, fontWeight: 500, fontSize: 15 }}
        >
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              mt: '6px',
              borderRadius: '8px',
              border: 1,
              borderColor: 'divider',
              boxShadow: 6,
            },
          },
          list: { sx: { py: 0 } },
        }}
      >
        {header}
        {populatedGroups.flatMap((group, groupIndex) => {
          const items = group.map((item) => {
            if (item.disabled) {
              return (
                <MenuItem key={item.href} disabled sx={itemSx}>
                  {item.label}
                </MenuItem>
              );
            }
            const itemDocProps = emitDocAttributes
              ? { 'data-doc-action': 'navigate', 'data-doc-context': item.href }
              : {};
            let externalLinkProps = {};
            if (item.newTab) {
              externalLinkProps = { target: '_blank', rel: 'noopener noreferrer' };
            }
            return (
              <MenuItem
                key={item.href}
                component="a"
                href={item.href}
                {...itemDocProps}
                {...externalLinkProps}
                sx={itemSx}
              >
                {item.label}
              </MenuItem>
            );
          });
          if (groupIndex > 0) {
            return [<Divider key={`divider-${groupIndex}`} sx={{ my: 0 }} />, ...items];
          }
          return items;
        })}
        {logout !== null && populatedGroups.length > 0 && <Divider sx={{ my: 0 }} />}
        {logout}
      </Menu>
    </>
  );
}
